import { Div, Label, Loader, useWindowSize } from "@grassrootly/rn-core";
import { ScrollView, Image, RefreshControl, View } from "react-native";
import { useCompetitionStandings } from "./useCompetitionStandings";
import { images } from "../../layout/assets";
import { MainStackScreenProps } from "../../navigation/Navigation.types";

interface CompetitionStandingsTabProps {
  competitionId: number;
  seasonId: number;
  navigation: MainStackScreenProps<"competition-detail">["navigation"];
}

export default function CompetitionStandingsTab({ competitionId, seasonId, navigation }: CompetitionStandingsTabProps) {
  const { standings, loading, refreshStandings } = useCompetitionStandings(competitionId, seasonId);
  const { width } = useWindowSize();

  const isMobile = width <= 720;

  return (
    <Div classNames={["flex1"]}>
      {loading && standings.length === 0 ? (
        <Div classNames={["flex1", "alignItemsCenter", "justifyContentCenter", "mt-{30}"]}>
          <Loader />
        </Div>
      ) : null}

      <ScrollView refreshControl={<RefreshControl refreshing={loading} onRefresh={refreshStandings} />} showsVerticalScrollIndicator={false}>
        {!loading && standings.length === 0 && (
          <Div classNames={["flex1", "alignItemsCenter", "justifyContentCenter", "mt-{30}"]}>
            <Label type="l2Regular" classNames={["colorSecondaryLight", "textCenter"]}>
              No standings data available
            </Label>
          </Div>
        )}

        {standings.length > 0 && (
          <Div classNames={["mt-{12}"]}>
            {/* Table Header */}
            <Div classNames={[
              "flexRow", 
              "alignItemsCenter", 
              "bgSecondaryMain", 
              isMobile ? "p8" : "p12",
              "mb-{4}"
            ]}>
              <View style={{ width: isMobile ? 30 : 40 }}>
                <Label type={isMobile ? "l4Bold" : "l3Bold"} classNames={["colorSecondaryLight"]}>#</Label>
              </View>
              <Div classNames={["flex1"]}>
                <Label type={isMobile ? "l4Bold" : "l3Bold"} classNames={["colorSecondaryLight"]}>Team</Label>
              </Div>
              <View style={{ width: isMobile ? 30 : 40, alignItems: 'center' }}>
                <Label type={isMobile ? "l4Bold" : "l3Bold"} classNames={["colorSecondaryLight"]}>W</Label>
              </View>
              <View style={{ width: isMobile ? 30 : 40, alignItems: 'center' }}>
                <Label type={isMobile ? "l4Bold" : "l3Bold"} classNames={["colorSecondaryLight"]}>D</Label>
              </View>
              <View style={{ width: isMobile ? 30 : 40, alignItems: 'center' }}>
                <Label type={isMobile ? "l4Bold" : "l3Bold"} classNames={["colorSecondaryLight"]}>L</Label>
              </View>
              <View style={{ width: isMobile ? 50 : 60, alignItems: 'center' }}>
                <Label type={isMobile ? "l4Bold" : "l3Bold"} classNames={["colorSecondaryLight"]}>Goals</Label>
              </View>
              <View style={{ width: isMobile ? 35 : 45, alignItems: 'center' }}>
                <Label type={isMobile ? "l4Bold" : "l3Bold"} classNames={["colorPrimary"]}>Pts</Label>
              </View>
            </Div>

            {/* Table Rows */}
            {standings.map((team, index) => (
              <Div 
                key={`${team.group_id}-${index}`}
                classNames={[
                  "flexRow", 
                  "alignItemsCenter", 
                  isMobile ? "p8" : "p12",
                  index % 2 !== 0 ? "bgSecondaryMain" : "",
                  "mb-{2}"
                ]}
              >
                {/* Position */}
                <View style={{ width: isMobile ? 30 : 40 }}>
                  <Label type={isMobile ? "l3Bold" : "l2Bold"} classNames={["colorSecondaryContrast"]}>
                    {team.position}
                  </Label>
                </View>

                {/* Team Logo & Name */}
                <Div classNames={["flex1", "flexRow", "alignItemsCenter", "gap8"]}>
                  <Image
                    source={team.team_logo ? { uri: team.team_logo } : images.groupAvatar}
                    style={{ 
                      width: isMobile ? 24 : 32, 
                      height: isMobile ? 24 : 32, 
                      borderRadius: isMobile ? 12 : 16 
                    }}
                    resizeMode="cover"
                  />
                  <Label 
                    type={isMobile ? "l3Regular" : "l2Regular"} 
                    classNames={["colorSecondaryContrast"]}
                    numberOfLines={1}
                  >
                    {team.team_name}
                  </Label>
                </Div>

                {/* Wins */}
                <View style={{ width: isMobile ? 30 : 40, alignItems: 'center' }}>
                  <Label type={isMobile ? "l3Regular" : "l2Regular"} classNames={["colorSecondaryContrast"]}>
                    {team.wins}
                  </Label>
                </View>

                {/* Draws */}
                <View style={{ width: isMobile ? 30 : 40, alignItems: 'center' }}>
                  <Label type={isMobile ? "l3Regular" : "l2Regular"} classNames={["colorSecondaryContrast"]}>
                    {team.draws}
                  </Label>
                </View>

                {/* Losses */}
                <View style={{ width: isMobile ? 30 : 40, alignItems: 'center' }}>
                  <Label type={isMobile ? "l3Regular" : "l2Regular"} classNames={["colorSecondaryContrast"]}>
                    {team.losses}
                  </Label>
                </View>

                {/* Goals For:Against */}
                <View style={{ width: isMobile ? 50 : 60, alignItems: 'center' }}>
                  <Label type={isMobile ? "l3Regular" : "l2Regular"} classNames={["colorSecondaryContrast"]}>
                    {team.goals_scored}:{team.goals_received}
                  </Label>
                </View>

                {/* Points */}
                <View style={{ width: isMobile ? 35 : 45, alignItems: 'center' }}>
                  <Div classNames={["bgPrimaryWashed", "p8", "rounded"]}>
                    <Label type={isMobile ? "l3Bold" : "l2Bold"} classNames={["colorPrimary"]}>
                      {team.points}
                    </Label>
                  </Div>
                </View>
              </Div>
            ))}
          </Div>
        )}
      </ScrollView>
    </Div>
  );
}
